import React, { useEffect, useState, useMemo } from 'react';
import moment from 'moment';
import { UATChecklistItem, UATFormData, UATResponse } from '../../data/types';
import { capitalizeString, generateUATResponse } from '../../utils/helpers';
import homecook_logo from '../../assets/img/homecook_logo.png'
import { Disclosure } from '@headlessui/react';
import { ChevronUpIcon, CheckCircleIcon, XCircleIcon, ClockIcon, DocumentCheckIcon } from '@heroicons/react/24/outline';
import { initialChecklistData, UAT_DRAFT_STORAGE_KEY } from '../../data/initialChecklist';
import axios from 'axios';
import config from '../../config';
import { useSubmitUATResponse } from '../../hooks/communityhook';
import LoadingScreen from './LoadingScreen';
import { useAuthState } from '../../contexts/authcontext';

const APIStatusIndicator = () => {
  const [status, setStatus] = useState<'online' | 'offline' | 'checking'>('checking');
  const [lastChecked, setLastChecked] = useState<string>('');

  const checkApiStatus = async () => {
    try {
      setStatus('checking');
      const response = await axios.get(`${config.apiUrl}/api/docs/`);
      // console.log('response', response);
      setStatus(response.status === 200 ? 'online' : 'offline');
    } catch (error) {
      setStatus('offline');
    }
    setLastChecked(moment().format('HH:mm:ss'));
  };

  useEffect(() => {
    checkApiStatus();
    const interval = setInterval(checkApiStatus, 1800000); // Check every 30 minutes
    return () => clearInterval(interval);
  }, []);

  return (
    <div className="flex items-center space-x-2 font-open-sans">
      <div className="flex items-center space-x-1">
        
        <span className="flex flex-row items-center font-open-sans space-x-1 text-sm font-medium text-gray-700">
          {capitalizeString(process.env.REACT_APP_NODE_ENV || 'dev')} Server Status: 
          <div className={`w-2 h-2 ml-3 rounded-full ${
            status === 'online' ? 'bg-green-500' : 
            status === 'offline' ? 'bg-red-500' : 
            'bg-yellow-500'
          }`} />
          <p className={`text-sm font-medium ${
            status === 'online' ? 'text-green-500' : 
            status === 'offline' ? 'text-red-500' : 
            'text-yellow-500'
          }`}>{status.charAt(0).toUpperCase() + status.slice(1)}</p>
        </span>
      </div>
      {lastChecked && (
        <span className="text-xs text-gray-500">
          Last checked: <span className='font-light'>{moment(lastChecked, 'HH:mm:ss').fromNow()}</span>
        </span>
      )}
    </div>
  );
};

const UATChecklist = () => {
  // const [minLoadingTimeElapsed, setMinLoadingTimeElapsed] = useState(false);
  const [isSubmitted, setIsSubmitted] = useState(false);
  const [saveSuccess, setSaveSuccess] = useState(false);

  useEffect(() => {
    document.title = 'HomeCook | Platform UAT Checklist';
    // check if there is a draftUATResponse in local storage
    const draftUATResponse = localStorage.getItem(UAT_DRAFT_STORAGE_KEY);
    // console.log('draftUATResponse', draftUATResponse);
    if (draftUATResponse) {
      const draftUATResponseJSON = JSON.parse(draftUATResponse) as UATResponse;
      setFormData(draftUATResponseJSON as unknown as UATFormData);
    }
  }, []);

  const onSave = (data: UATFormData) => {
    const uatResponse = generateUATResponse(data, true);
    const uatResponseJSON = JSON.stringify(uatResponse);
    localStorage.setItem(UAT_DRAFT_STORAGE_KEY, uatResponseJSON);
    setSaveSuccess(true);
    setTimeout(() => {
      setSaveSuccess(false);
    }, 3000);
  };

  const { submitUATResponseData, isLoading, isError, error, isSuccess } = useSubmitUATResponse();
  var { authUser } = useAuthState()
  
  const onSubmit = (data: UATFormData) => {
    if (!authUser?.username && !data.email_address) {
      return;
    }
    const testerEmail = authUser?.username || data.email_address || '';
    data.test_environment = process.env.REACT_APP_NODE_ENV || 'dev';
    data.sign_off_status.product_owner = testerEmail;
    data.sign_off_status.technical_lead = testerEmail;
    data.sign_off_status.dates.qa_lead = moment().format('YYYY-MM-DD');
    data.sign_off_status.dates.product_owner = moment().format('YYYY-MM-DD');
    data.sign_off_status.dates.technical_lead = moment().format('YYYY-MM-DD');
    const uatResponse = generateUATResponse(data);
    submitUATResponseData({
      email_address: testerEmail,
      uatResponse
    });
    const timer = setTimeout(() => {
      setIsSubmitted(true);
    }, 1000);

    localStorage.removeItem(UAT_DRAFT_STORAGE_KEY);
    setFormData(initialChecklistData);

    return () => clearTimeout(timer);
  };


  const [formData, setFormData] = useState<UATFormData>(initialChecklistData || {
    categories: [],
    test_environment: '',
    test_period: {
      start_date: '',
      end_date: '',
    },
    testers: [],
    sign_off_status: {
      qa_lead: '',
      product_owner: '',
      technical_lead: '',
      dates: {
        qa_lead: '',
        product_owner: '',
        technical_lead: '',
      },
    },
    email_address: '',
  });

  const calculateProgress = useMemo(() => {
    let totalItems = 0;
    let completedItems = 0;
    let failedItems = 0;
    let excludedItems = 0;

    formData.categories.forEach(category => {
      category.items.forEach(item => {
        if (category.id !== '1') { // Exclude environment setup items
          totalItems++;
          if (item.status === 'successful') completedItems++;
          if (item.status === 'failed') failedItems++;
          if (item.status === 'excluded') excludedItems++;
        }
      });
    });

    const activeItems = totalItems - excludedItems;
    const progress = activeItems > 0 ? (completedItems / activeItems) * 100 : 0;

    return {
      progress,
      totalItems,
      completedItems,
      failedItems,
      excludedItems,
      activeItems
    };
  }, [formData]);

  const handleItemStatusChange = (categoryId: string, itemId: string, status: UATChecklistItem['status']) => {
    setFormData(prev => ({
      ...prev,
      categories: prev.categories.map(category => {
        if (category.id === categoryId) {
          return {
            ...category,
            items: category.items.map(item => {
              if (item.id === itemId) {
                return { ...item, status };
              }
              return item;
            }),
          };
        }
        return category;
      }),
    }));
  };

  const handleItemNotesChange = (categoryId: string, itemId: string, notes: string) => {
    setFormData(prev => ({
      ...prev,
      categories: prev.categories.map(category => {
        if (category.id === categoryId) {
          return {
            ...category,
            items: category.items.map(item => {
              if (item.id === itemId) {
                return { ...item, notes };
              }
              return item;
            }),
          };
        }
        return category;
      }),
    }));
  };

  const handleItemPriorityChange = (categoryId: string, itemId: string, priority: 'P0' | 'P1' | 'P2' | 'P3') => {
    setFormData(prev => ({
      ...prev,
      categories: prev.categories.map(category => {
        if (category.id === categoryId) {
          return {
            ...category,
            items: category.items.map(item => {
              if (item.id === itemId) {
                return { ...item, priority };
              }
              return item;
            }),
          };
        }
        return category;
      }),
    }));
  };

  const getStatusIcon = (status: UATChecklistItem['status']) => {
    switch (status) {
      case 'successful':
        return <CheckCircleIcon className="w-5 h-5 text-green-500" />;
      case 'failed':
        return <XCircleIcon className="w-5 h-5 text-red-500" />;
      case 'excluded':
        return <XCircleIcon className="w-5 h-5 text-gray-400" />;
      default:
        return <ClockIcon className="w-5 h-5 text-yellow-500" />;
    }
  };

  const getPriorityBadgeStyle = (priority: 'P0' | 'P1' | 'P2' | 'P3') => {
    switch (priority) {
      case 'P0':
        return 'bg-red-100 text-red-800';
      case 'P1':
        return 'bg-orange-100 text-orange-800';
      case 'P2':
        return 'bg-yellow-100 text-yellow-800';
      default:
        return 'bg-green-100 text-green-800';
    }
  };

  if (isLoading && !isSubmitted) {
    return <LoadingScreen />
  }
  if (isError) {
    return (
      <div className="min-h-screen bg-gray-50 flex items-center justify-center">
        <div className="max-w-md w-full bg-white rounded-lg shadow-sm p-8 border border-gray-100 text-center">
          <XCircleIcon className="w-16 h-16 text-red-500 mx-auto mb-4" />
          <h2 className="text-2xl font-semibold text-gray-900 mb-2 font-raleway">Submission Failed</h2>
          <p className="text-gray-600 mb-6 font-open-sans">{error?.message || 'An error occurred while submitting the report. Please try again.'}</p>
          <div className="space-y-3">
            <button
              onClick={() => window.location.reload()}
              className="w-full px-6 py-3 bg-primary-200 text-white font-medium rounded-lg hover:bg-primary-300 focus:outline-none focus:ring-2 focus:ring-primary-200 focus:ring-offset-2 transition-colors shadow-sm font-raleway"
            >
              Try Again
            </button>
            <button
              onClick={() => onSave?.(formData)}
              className="w-full px-6 py-3 bg-white text-primary-200 border border-gray-200 font-medium rounded-lg hover:bg-gray-50 focus:outline-none focus:ring-2 focus:ring-primary-200 focus:ring-offset-2 transition-colors shadow-sm font-raleway"
            >
              Save as Draft
            </button>
          </div>
        </div>
      </div>
    );
  }
  if (isSuccess || isSubmitted) {
    return (
      <div className="min-h-screen bg-gray-50 flex items-center justify-center">
        <div className="max-w-md w-full bg-white rounded-lg shadow-sm p-8 border border-gray-100 text-center">
          <CheckCircleIcon className="w-16 h-16 text-green-500 mx-auto mb-4" />
          <h2 className="text-2xl font-semibold text-gray-900 mb-2 font-raleway">Report Submitted Successfully</h2>
          <p className="text-gray-600 mb-6 font-open-sans">Your UAT report has been submitted successfully. Thank you for your contribution!</p>
          <div className="space-y-3">
            <button
              onClick={() => {
                window.location.reload();
              }}
              className="w-full px-6 py-3 bg-primary-200 text-white font-medium rounded-lg hover:bg-primary-300 focus:outline-none focus:ring-2 focus:ring-primary-200 focus:ring-offset-2 transition-colors shadow-sm font-raleway"
            >
              Submit Another Report
            </button>
            <button
              onClick={() => window.location.href = '/'}
              className="w-full px-6 py-3 bg-white text-primary-200 border border-gray-200 font-medium rounded-lg hover:bg-gray-50 focus:outline-none focus:ring-2 focus:ring-primary-200 focus:ring-offset-2 transition-colors shadow-sm font-raleway"
            >
              Return to Dashboard
            </button>
          </div>
        </div>
      </div>
    );
  }
  return (
    <div className="min-h-screen bg-gray-50">
      <div className="max-w-7xl mx-auto px-4 sm:px-6 py-6">
        <div className="space-y-6">
          {/* Header */}
          <div className="text-center bg-white rounded-lg shadow-sm p-8 border border-gray-100">
            {/* <img src={homecook_logo} alt="HomeCook Logo" className="w-10 h-10 mb-3" /> */}
            <img src={homecook_logo} alt="HomeCook" className='w-[100px] mx-auto cursor-pointer' onClick={()=> null} />
            <h1 className="text-2xl font-semibold text-gray-900 mb-1 font-raleway">
              HomeCook - User Acceptance Testing
            </h1>
            <p className="text-lg text-gray-600 font-open-sans">
              Shared-kitchen ops: memberships, station booking, inventory, and payments
            </p>
            {/* <p className="mt-2 max-w-2xl mx-auto text-sm text-gray-500 font-open-sans">
              Walk the partner dashboard and member app in journey order. Mark unused integrations or resource types as Exclude. Category 1 is environment only and does not count toward progress.
            </p> */}

            <div className="mt-2 flex justify-center">
              <APIStatusIndicator />
            </div>
          </div>

          {/* Test Environment Info */}
          <div className="bg-white rounded-lg shadow-sm p-6 border border-gray-100 flex flex-col gap-4">
            {/* <h2 className="text-2xl font-medium text-gray-900 mb-6 font-raleway">
              Test Environment Information
            </h2> */}
            <div className="grid grid-cols-1 md:grid-cols-2 gap-6">
              <div>
                <label className="block text-sm font-medium text-gray-700 mb-2 font-open-sans">
                  App Stage
                </label>
                <select
                  className="w-full cursor-not-allowed px-4 font-open-sans py-2 border-[1px] border-[#E9E9E9] bg-white rounded-md focus:ring-primary-300 focus:border-[#E9E9E9] transition-colors"
                  value={process.env.REACT_APP_NODE_ENV || formData.test_environment}
                  disabled={!!process.env.REACT_APP_NODE_ENV}
                  onChange={(e) =>
                    setFormData(prev => ({
                      ...prev,
                      test_environment: e.target.value,
                    }))
                  }
                >
                  <option value="" disabled>-- Select Testing Environment --</option>
                  <option value="dev">Development</option>
                  <option value="test">QA</option>
                  <option value="prod">Production</option>
                </select>
              </div>
              <div className='ml-auto'>
                <label className="block text-sm font-medium text-gray-700 mb-2 font-open-sans">
                  Report Date
                </label>
                <div className="grid grid-cols-2 gap-4 font-open-sans">
                  <input
                    type="date"
                    className="w-full cursor-not-allowed px-4 py-2 border-[1px] border-[#E9E9E9] rounded-md focus:ring-primary-300 focus:border-[#E9E9E9] transition-colors"
                    value={moment().format('YYYY-MM-DD')}
                    disabled
                    // onChange={(e) =>
                    //   setFormData(prev => ({
                    //     ...prev,
                    //     test_period: {
                    //       ...prev.test_period,
                    //       start_date: e.target.value,
                    //     },
                    //   }))
                    // }
                  />
                  {/* <input
                    type="date"
                    className="w-full px-4 py-2 border-[1px] border-[#E9E9E9] rounded-md focus:ring-primary-300 focus:border-[#E9E9E9] transition-colors"
                    value={formData.test_period.end_date}
                    onChange={(e) =>
                      setFormData(prev => ({
                        ...prev,
                        test_period: {
                          ...prev.test_period,
                          end_date: e.target.value,
                        },
                      }))
                    }
                  /> */}
                </div>
              </div>
            </div>
          </div>
          {/* Progress Bar */}
          <div className="bg-white rounded-lg shadow-sm p-6 border border-gray-100">
            <div className="flex items-center justify-between mb-4">
              <div className="flex items-center space-x-2">
                <DocumentCheckIcon className="w-6 h-6 text-primary-200" />
                <h2 className="text-lg font-medium text-gray-900 font-raleway">Testing Progress</h2>
              </div>
              <div className="text-sm text-gray-600 font-open-sans">
                {calculateProgress.completedItems} of {calculateProgress.activeItems} items tested successfully
              </div>
            </div>
            <div className="w-full bg-gray-200 rounded-full h-2.5">
              <div 
                className="h-2.5 rounded-full transition-all duration-500 ease-in-out"
                style={{ 
                  width: `${calculateProgress.progress}%`,
                  backgroundColor: calculateProgress.progress === 100 ? '#10B981' : '#3B82F6'
                }}
              />
            </div>
            <div className="grid grid-cols-4 gap-4 mt-4 font-raleway">
              <div className="bg-green-50 p-3 rounded-lg">
                <div className="text-md font-medium text-green-800">Successful</div>
                <div className="text-2xl font-bold text-green-600">{calculateProgress.completedItems}</div>
              </div>
              <div className="bg-red-50 p-3 rounded-lg">
                <div className="text-md font-medium text-red-800">Failed</div>
                <div className="text-2xl font-bold text-red-600">{calculateProgress.failedItems}</div>
              </div>
              <div className="bg-yellow-50 p-3 rounded-lg">
                <div className="text-md font-medium text-yellow-800">Pending</div>
                <div className="text-2xl font-bold text-yellow-600">
                  {calculateProgress.activeItems - calculateProgress.completedItems - calculateProgress.failedItems}
                </div>
              </div>
              <div className="bg-gray-50 p-3 rounded-lg">
                <div className="text-md font-medium text-gray-800">Excluded</div>
                <div className="text-2xl font-bold text-gray-600">{calculateProgress.excludedItems}</div>
              </div>
            </div>


            {/* Priority Legend */}
            <h3 className="text-sm font-medium text-gray-900 mb-3 mt-5 font-raleway">Priority Legend</h3>
            <div className="flex flex-wrap gap-4">
              <div className="flex items-center space-x-2">
                <span className={`px-2.5 py-0.5 text-xs font-medium rounded-full font-open-sans bg-red-100 text-red-800`}>P0</span>
                <span className="text-sm text-gray-600 font-open-sans">Critical</span>
              </div>
              <div className="flex items-center space-x-2">
                <span className={`px-2.5 py-0.5 text-xs font-medium rounded-full font-open-sans bg-orange-100 text-orange-800`}>P1</span>
                <span className="text-sm text-gray-600 font-open-sans">High</span>
              </div>
              <div className="flex items-center space-x-2">
                <span className={`px-2.5 py-0.5 text-xs font-medium rounded-full font-open-sans bg-yellow-100 text-yellow-800`}>P2</span>
                <span className="text-sm text-gray-600 font-open-sans">Medium</span>
              </div>
              <div className="flex items-center space-x-2">
                <span className={`px-2.5 py-0.5 text-xs font-medium rounded-full font-open-sans bg-green-100 text-green-800`}>P3</span>
                <span className="text-sm text-gray-600 font-open-sans">Low</span>
              </div>
            </div>
          </div>

          
          {/* <div className="bg-white rounded-lg shadow-sm p-4 border border-gray-100">
          </div> */}

          {/* Checklist Categories */}
          <div className="space-y-4">
            {formData.categories.map((category) => (
              <Disclosure key={category.id}>
                {({ open }) => (
                  <div className={`bg-white rounded-lg shadow-sm border border-gray-100 overflow-hidden ${open ? 'bg-gray-50' : ''}`}>
                    <Disclosure.Button className={`w-full px-6 py-4 text-left transition-colors ${open ? 'bg-gray-50' : 'bg-white hover:bg-gray-50'}`}>
                      <div className="flex justify-between items-center">
                        <div>
                          <div className="flex items-center space-x-3">
                            <h3 className="text-xl font-medium text-gray-900 font-raleway">
                              {category.title}
                            </h3>
                            {(category.id !== '1' && category.id !== '9') &&(<span className={`px-2.5 py-0.5 text-xs font-medium rounded-full font-open-sans ${
                              category.user_type === 'partner' 
                                ? 'bg-blue-100 text-blue-800' 
                                : 'bg-purple-100 text-purple-800'
                            }`}>
                              {category.user_type === 'partner' ? 'Partner' : 'Member'}
                            </span>)}
                          </div>
                          <p className="text-sm text-gray-600 mt-1 font-open-sans">{category.description}</p>
                        </div>
                        <ChevronUpIcon
                          className={`${
                            open ? 'transform rotate-180' : ''
                          } w-6 h-6 text-gray-500 transition-transform duration-200`}
                        />
                      </div>
                    </Disclosure.Button>
                    <Disclosure.Panel className="px-6 pb-6">
                      <div className="space-y-6">
                        {category.items.map((item) => {
                          let customInput: React.ReactNode = null;
                          // let customInputLabel = '';
                          if (category.id === '1') {
                            if (item.title === 'Device Type') {
                              // customInputLabel = 'Select Device Type';
                              customInput = (
                                <select
                                  className="w-1/8 text-sm py-2 border-[1px] border-[#E9E9E9] bg-white rounded-md focus:ring-primary-300 focus:border-[#E9E9E9] transition-colors"
                                  value={item.notes || ''}
                                  onChange={(e) => handleItemNotesChange(category.id, item.id, e.target.value)}
                                >
                                  <option value="" disabled>Select device type</option>
                                  <option value="Desktop">Desktop</option>
                                  <option value="Tablet">Tablet</option>
                                  <option value="Mobile">Mobile</option>
                                </select>
                              );
                            } else if (item.title === 'Web Browser') {
                              // customInputLabel = 'Select Web Browser';
                              customInput = (
                                <select
                                  className="w-1/8 text-sm py-2 border-[1px] border-[#E9E9E9] bg-white rounded-md focus:ring-primary-300 focus:border-[#E9E9E9] transition-colors"
                                  value={item.notes || ''}
                                  onChange={(e) => handleItemNotesChange(category.id, item.id, e.target.value)}
                                >
                                  <option value="" disabled>Select browser</option>
                                  <option value="Chrome">Chrome</option>
                                  <option value="Firefox">Firefox</option>
                                  <option value="Safari">Safari</option>
                                  <option value="Edge">Edge</option>
                                  <option value="Other">Other</option>
                                </select>
                              );
                            } else if (item.title === 'Operating System') {
                              // customInputLabel = 'Select Operating System';
                              customInput = (
                                <select
                                  className="w-1/8 text-sm py-2 border-[1px] border-[#E9E9E9] bg-white rounded-md focus:ring-primary-300 focus:border-[#E9E9E9] transition-colors"
                                  value={item.notes || ''}
                                  onChange={(e) => handleItemNotesChange(category.id, item.id, e.target.value)}
                                >
                                  <option value="" disabled>Select OS</option>
                                  <option value="Windows">Windows</option>
                                  <option value="macOS">macOS</option>
                                  <option value="Linux">Linux</option>
                                  <option value="iOS">iOS</option>
                                  <option value="Android">Android</option>
                                  <option value="Other">Other</option>
                                </select>
                              );
                            }
                          }
                          // if (category.id === '4') {
                          //   if (item.title === 'Kitchen Availability') {
                          //     customInputLabel = 'Select Kitchen Availability (Days)';
                          //     customInput = (
                          //       <div className="flex flex-col space-y-2">
                          //         <select
                          //           multiple
                          //           className="w-full px-2 py-1 border border-blue-200 bg-blue-50 rounded-md"
                          //           value={item.notes ? item.notes.split(',') : []}
                          //           onChange={(e) => {
                          //             const selected = Array.from(e.target.selectedOptions).map(opt => opt.value);
                          //             handleItemNotesChange(category.id, item.id, selected.join(','));
                          //           }}
                          //         >
                          //           <option value="Monday">Monday</option>
                          //           <option value="Tuesday">Tuesday</option>
                          //           <option value="Wednesday">Wednesday</option>
                          //           <option value="Thursday">Thursday</option>
                          //           <option value="Friday">Friday</option>
                          //           <option value="Saturday">Saturday</option>
                          //           <option value="Sunday">Sunday</option>
                          //         </select>
                          //       </div>
                          //     );
                          //   } else if (item.title === 'Operating Hours') {
                          //     customInputLabel = 'Set Kitchen Operating Hours';
                          //     customInput = (
                          //       <div className="flex flex-col space-y-2">
                          //         <div className="flex space-x-2">
                          //           <input
                          //             type="time"
                          //             className="px-2 py-1 border border-blue-200 bg-blue-50 rounded-md"
                          //             value={item.notes ? item.notes.split('-')[0] : ''}
                          //             onChange={(e) => {
                          //               const end = item.notes ? item.notes.split('-')[1] || '' : '';
                          //               handleItemNotesChange(category.id, item.id, `${e.target.value}-${end}`);
                          //             }}
                          //           />
                          //           <span className="self-center">to</span>
                          //           <input
                          //             type="time"
                          //             className="px-2 py-1 border border-blue-200 bg-blue-50 rounded-md"
                          //             value={item.notes ? item.notes.split('-')[1] || '' : ''}
                          //             onChange={(e) => {
                          //               const start = item.notes ? item.notes.split('-')[0] || '' : '';
                          //               handleItemNotesChange(category.id, item.id, `${start}-${e.target.value}`);
                          //             }}
                          //           />
                          //         </div>
                          //       </div>
                          //     );
                          //   }
                          // }
                          return (
                            <div
                              key={item.id}
                              className="border-t border-gray-100 pt-6"
                            >
                              <div className="flex items-start justify-between">
                                <div className="flex-1">
                                  <div className="flex items-center space-x-3">
                                    <h4 className="text-lg font-medium text-gray-900 font-raleway">
                                      {item.title}
                                    </h4>
                                    {
                                      (category.id !== '1' && category.id !== '9') && (
                                        <select
                                          className={`px-2.5 py-0.5 min-w-[50px] text-xs font-medium rounded-full font-open-sans ${getPriorityBadgeStyle(item.priority)} border-0 focus:ring-0`}
                                          value={item.priority}
                                          onChange={(e) => handleItemPriorityChange(category.id, item.id, e.target.value as 'P0' | 'P1' | 'P2' | 'P3')}
                                        >
                                          <option value="P0">P0</option>
                                          <option value="P1">P1</option>
                                          <option value="P2">P2</option>
                                          <option value="P3">P3</option>
                                        </select>
                                      )
                                    }
                                  </div>
                                  <p className="text-sm text-gray-600 mt-1 font-open-sans">
                                    {item.description}
                                  </p>
                                  {/* Render custom input for selection-based items, with label, outside notes disclosure */}
                                  {customInput && (
                                    <div className="mt-3">
                                      {/* <label className="block text-xs font-light text-primary-200 mb-1 tracking-wide font-open-sans">{customInputLabel}</label> */}
                                      <div className="rounded-lg shadow-sm">
                                        {/* Dropdown or custom input styling */}
                                        {React.cloneElement(customInput as React.ReactElement, {
                                          className: `${(customInput as React.ReactElement).props.className || ''} font-open-sans border-primary-300/20 bg-primary-300/20 text-primary-200 font-medium rounded-lg focus:ring-primary-300 focus:border-[#E9E9E9] transition-colors shadow-none`,
                                        })}
                                      </div>
                                    </div>
                                  )}
                                </div>
                               {(category.id !== '1') && ( <div className="ml-6 flex flex-col items-end space-y-4 font-open-sans">
                                  <div className="flex items-center space-x-3">
                                    {getStatusIcon(item.status)}
                                    <select
                                      className="px-4 py-2 border border-gray-300 rounded-lg focus:ring-2 focus:ring-primary-200 focus:border-primary-500 transition-colors bg-white shadow-sm font-medium"
                                      value={item.status}
                                      onChange={(e) =>
                                        handleItemStatusChange(
                                          category.id,
                                          item.id,
                                          e.target.value as UATChecklistItem['status']
                                        )
                                      }
                                    >
                                      <option value="pending">Pending</option>
                                      <option value="successful">Successful</option>
                                      <option value="failed">Failed</option>
                                      <option value="excluded">Exclude from test</option>
                                    </select>
                                  </div>
                                  <Disclosure>
                                    {({ open }) => (
                                      <div className="w-full">
                                        <Disclosure.Button className="flex items-center space-x-2 text-sm text-gray-500 hover:text-gray-700 transition-colors">
                                          <span className="font-medium">{open ? 'Hide notes' : 'Add notes'}</span>
                                          <ChevronUpIcon
                                            className={`${open ? 'transform rotate-180' : ''} w-4 h-4 transition-transform duration-200`}
                                          />
                                        </Disclosure.Button>
                                        <Disclosure.Panel className="mt-3">
                                          <div className="bg-gray-50 rounded-lg p-4">
                                            <label className="block text-sm font-medium text-gray-700 mb-2">Test Notes</label>
                                            <textarea
                                              className="w-full text-sm px-4 py-2 border border-gray-200 bg-white text-gray-700 rounded-lg focus:ring-2 focus:ring-primary-200 focus:border-primary-500 transition-colors"
                                              placeholder="Add your test notes here..."
                                              value={item.notes || ''}
                                              onChange={(e) =>
                                                handleItemNotesChange(
                                                  category.id,
                                                  item.id,
                                                  e.target.value
                                                )
                                              }
                                              rows={3}
                                            />
                                          </div>
                                        </Disclosure.Panel>
                                      </div>
                                    )}
                                  </Disclosure>
                                </div>)}
                              </div>
                            </div>
                          );
                        })}
                      </div>
                    </Disclosure.Panel>
                  </div>
                )}
              </Disclosure>
            ))}
          </div>

          {/* Sign-off Section */}
          {/* <div className="bg-white rounded-lg shadow-sm p-6 border border-gray-100">
            <h2 className="text-2xl font-medium text-gray-900 mb-6 font-raleway">
              Sign-off Status
            </h2>
            <div className="grid grid-cols-1 md:grid-cols-3 gap-6">
              <div>
                <label className="block text-sm font-medium text-gray-700 mb-2 font-open-sans">
                  QA Lead
                </label>
                <input
                  type="text"
                  className="w-full font-open-sans px-4 py-2 border border-gray-300 rounded-md focus:ring-2 focus:ring-blue-500 focus:border-blue-500 transition-colors mb-3"
                  value={formData.sign_off_status.qa_lead}
                  onChange={(e) =>
                    setFormData(prev => ({
                      ...prev,
                      sign_off_status: {
                        ...prev.sign_off_status,
                        qa_lead: e.target.value,
                      },
                    }))
                  }
                />
                <input
                  type="date"
                  className="w-full font-open-sans px-4 py-2 border border-gray-300 rounded-md focus:ring-2 focus:ring-blue-500 focus:border-blue-500 transition-colors"
                  value={formData.sign_off_status.dates.qa_lead}
                  onChange={(e) =>
                    setFormData(prev => ({
                      ...prev,
                      sign_off_status: {
                        ...prev.sign_off_status,
                        dates: {
                          ...prev.sign_off_status.dates,
                          qa_lead: e.target.value,
                        },
                      },
                    }))
                  }
                />
              </div>
              <div>
                <label className="block text-sm font-medium text-gray-700 mb-2 font-open-sans">
                  Product Owner
                </label>
                <input
                  type="text"
                  className="w-full font-open-sans px-4 py-2 border border-gray-300 rounded-md focus:ring-2 focus:ring-blue-500 focus:border-blue-500 transition-colors mb-3"
                  value={formData.sign_off_status.product_owner}
                  onChange={(e) =>
                    setFormData(prev => ({
                      ...prev,
                      sign_off_status: {
                        ...prev.sign_off_status,
                        product_owner: e.target.value,
                      },
                    }))
                  }
                />
                <input
                  type="date"
                  className="w-full font-open-sans px-4 py-2 border border-gray-300 rounded-md focus:ring-2 focus:ring-blue-500 focus:border-blue-500 transition-colors"
                  value={formData.sign_off_status.dates.product_owner}
                  onChange={(e) =>
                    setFormData(prev => ({
                      ...prev,
                      sign_off_status: {
                        ...prev.sign_off_status,
                        dates: {
                          ...prev.sign_off_status.dates,
                          product_owner: e.target.value,
                        },
                      },
                    }))
                  }
                />
              </div>
              <div>
                <label className="block text-sm font-medium text-gray-700 mb-2 font-open-sans">
                  Technical Lead
                </label>
                <input
                  type="text"
                  className="w-full font-open-sans px-4 py-2 border border-gray-300 rounded-md focus:ring-2 focus:ring-blue-500 focus:border-blue-500 transition-colors mb-3"
                  value={formData.sign_off_status.technical_lead}
                  onChange={(e) =>
                    setFormData(prev => ({
                      ...prev,
                      sign_off_status: {
                        ...prev.sign_off_status,
                        technical_lead: e.target.value,
                      },
                    }))
                  }
                />
                <input
                  type="date"
                  className="w-full font-open-sans px-4 py-2 border border-gray-300 rounded-md focus:ring-2 focus:ring-blue-500 focus:border-blue-500 transition-colors"
                  value={formData.sign_off_status.dates.technical_lead}
                  onChange={(e) =>
                    setFormData(prev => ({
                      ...prev,
                      sign_off_status: {
                        ...prev.sign_off_status,
                        dates: {
                          ...prev.sign_off_status.dates,
                          technical_lead: e.target.value,
                        },
                      },
                    }))
                  }
                />
              </div>
            </div>
          </div> */}

          {/* Add email field before the Save/Submit buttons */}
          <div className="bg-white rounded-lg shadow-sm p-6 border border-gray-100 mb-4">
            <h2 className="text-lg font-medium text-gray-900 mb-4 font-raleway">Tester Contact Information</h2>
            {(!authUser?.username) && (<div className="mb-4">
              <p className="text-sm text-gray-600 font-open-sans flex items-center gap-1">
                <span className="text-red-500">*</span>
                Email address is required to save draft or submit report
              </p>
            </div>)}
            <div>
              <label className="block text-sm font-medium text-gray-700 mb-2 font-open-sans">
                Email Address
              </label>
              <input
                type="email"
                required
                className={`w-full px-4 py-2 ${!!authUser?.username ? 'cursor-not-allowed' : ''} border-[1px] border-[#E9E9E9] text-gray-500 rounded-md focus:ring-1 focus:ring-primary-200 focus:border-primary-500 transition-colors font-open-sans`}
                placeholder="Enter your email address"
                value={authUser?.username || formData.email_address}
                disabled={!!authUser?.username}
                onChange={(e) =>
                  setFormData(prev => ({
                    ...prev,
                    email_address: e.target.value,
                  }))
                }
              />
            </div>
          </div>
          

          {/* Save Button */}
          <div className="flex justify-end gap-4">
            <div className="flex items-center gap-4">
              {saveSuccess && (
                <div className="text-sm text-green-600 font-open-sans flex items-center gap-1">
                  <CheckCircleIcon className="w-5 h-5" />
                  Draft saved successfully
                </div>
              )}
              <button

                className={`px-6 py-3 bg-white font-raleway text-primary-200 border-[1px] border-[#E9E9E9] font-medium rounded-lg  focus:outline-none focus:ring-0  transition-colors shadow-sm ${!(authUser?.username || formData.email_address) || saveSuccess ? 'cursor-not-allowed' : 'hover:bg-[#E9E9E9]'}`}
                onClick={() => onSave?.(formData)}
                disabled={!(authUser?.username || formData.email_address) || saveSuccess || isLoading}
              >
                Save Draft
              </button>
            </div>


            <button
              className={`px-6 py-3 bg-primary-200 font-raleway text-white font-medium rounded-lg focus:outline-none focus:ring-0 focus:ring-primary-200 focus:ring-offset-2 transition-colors shadow-sm ${!(authUser?.username || formData.email_address) ? 'cursor-not-allowed' : 'hover:bg-primary-300'}`}
              onClick={() => onSubmit?.(formData)}
              disabled={!(authUser?.username || formData.email_address)}
            >
              Submit Report
            </button>
          </div>
        </div>
      </div>
    </div>
  );
};

export default UATChecklist; 